import React, { useMemo, useCallback, useState } from 'react';
import { Card, Tooltip } from 'reactstrap';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Button, Tag } from '@jem-open/jem-ui';
import { environment } from '../../../environment/environment';
import { useBulkSelection } from './useBulkSelection';
import { TableCheckbox } from './components';
import { BatchStatus } from './types/types';
import { DataTable } from '../../../components/DataTable';
import { EmptyState } from '../../../components/EmptyState';

const STATUS_TAG_VARIANT = {
  [BatchStatus.Ready]: 'success',
  [BatchStatus.Completed]: 'success',
  [BatchStatus.Processing]: 'processing',
  [BatchStatus.Pending]: 'pending',
  [BatchStatus.Failed]: 'failed',
  [BatchStatus.Archived]: 'neutral',
};

const AdvanceBatchList = (props) => {
  const {
    advanceBatches,
    handleShowBatchItemsModal,
    selectedEmployerId,
    onBulkSelectionChange,
    bulkSelection,
    isLoading = false,
  } = props;
  const [tooltipOpen, setTooltipOpen] = useState({});

  const safeAdvanceBatches = useMemo(
    () => (Array.isArray(advanceBatches) ? advanceBatches : []),
    [advanceBatches]
  );

  const transformedBatches = useMemo(
    () =>
      safeAdvanceBatches.map((batch) => ({
        id: parseInt(batch.id, 10),
        employerId: batch.employer?.id || '',
        status: batch.status,
        createdDate: batch.dateCreated
          ? new Date(batch.dateCreated)
          : new Date(),
        processedDate: batch.processedDate
          ? new Date(batch.processedDate)
          : undefined,
        fileName: `batch_${batch.id}.csv`,
        fileSize: 0,
        recordCount: batch.itemsCount || 0,
        downloadUrl:
          batch.status === BatchStatus.Ready
            ? `${environment.baseUrl}/advance_batch/${batch.id}`
            : undefined,
        isDownloadable: batch.status === BatchStatus.Ready,
      })),
    [safeAdvanceBatches]
  );

  const localBulkSelection = useBulkSelection(
    transformedBatches,
    bulkSelection?.selectedBatchIds || [],
    (selectedIds) => {
      if (onBulkSelectionChange) {
        onBulkSelectionChange({
          selectedBatchIds: selectedIds,
          selectedCount: selectedIds.length,
          canDownload: selectedIds.length > 0,
        });
      }
    }
  );
  const {
    selectedCount = 0,
    selectBatch = () => undefined,
    selectAll = () => undefined,
    isSelected = () => false,
    selectAllState = { checked: false, indeterminate: false },
  } = localBulkSelection || {};

  const hasBatches = safeAdvanceBatches.length > 0;
  const headerSelectAllState = hasBatches
    ? selectAllState
    : { checked: false, indeterminate: false };
  const totalDownloadableCount = hasBatches
    ? transformedBatches.filter((b) => b.isDownloadable).length
    : 0;

  const toggleTooltip = useCallback((batchId) => {
    setTooltipOpen((prev) => ({ ...prev, [batchId]: !prev[batchId] }));
  }, []);

  const handleToggleSelectAllDisabled = useCallback(() => {
    toggleTooltip('select-all-disabled');
  }, [toggleTooltip]);

  const createBatchSelectHandler = useCallback(
    (transformedBatchId) => () => selectBatch(transformedBatchId),
    [selectBatch]
  );

  const createShowBatchItemsHandler = useCallback(
    (batchId) => () => handleShowBatchItemsModal(batchId),
    [handleShowBatchItemsModal]
  );

  const createDownloadHandler = useCallback(
    (batchId) => () =>
      window.open(`${environment.baseUrl}/advance_batch/${batchId}`),
    []
  );

  const columns = [
    {
      key: 'select',
      width: '40px',
      className: 'text-center',
      label: (
        <>
          <span id="select-all-checkbox-target" className="inline-flex">
            <TableCheckbox
              id="select-all-checkbox"
              checked={headerSelectAllState.checked}
              onChange={selectAll}
              disabled={!selectedEmployerId || totalDownloadableCount === 0}
              indeterminate={headerSelectAllState.indeterminate}
              ariaLabel={`Select all batches (${selectedCount} of ${totalDownloadableCount} selected)`}
            />
          </span>
          {!selectedEmployerId && (
            <Tooltip
              placement="top"
              isOpen={tooltipOpen['select-all-disabled'] || false}
              target="select-all-checkbox-target"
              toggle={handleToggleSelectAllDisabled}>
              Select all is only available when filtering by employer
            </Tooltip>
          )}
        </>
      ),
      render: (value, batch) => {
        const transformedBatch = transformedBatches.find(
          (tb) => tb.id === parseInt(batch.id, 10)
        );
        return (
          <TableCheckbox
            id={`batch-checkbox-${batch.id}`}
            checked={isSelected(transformedBatch?.id)}
            onChange={createBatchSelectHandler(transformedBatch?.id)}
            disabled={!transformedBatch?.isDownloadable}
            ariaLabel={`Select batch ${
              transformedBatch?.fileName || batch.id
            }`}
          />
        );
      },
    },
    {
      key: 'id',
      label: 'ID',
      width: '80px',
    },
    {
      key: 'employer',
      label: 'Employer',
      width: '210px',
      className: 'max-w-[210px]',
      render: (value, batch) => (
        <div className="w-full truncate" title={batch.employer?.companyName}>
          {batch.employer?.companyName}
        </div>
      ),
    },
    {
      key: 'payroll',
      label: 'Payroll',
      width: '220px',
      className: 'max-w-[220px]',
      render: (value, batch) => (
        <div className="w-full truncate" title={batch.payroll?.name}>
          {batch.payroll?.name}
        </div>
      ),
    },
    {
      key: 'dateRange',
      label: 'Date Range',
      render: (value, batch) =>
        `${moment(batch.startDate).format('DD/MM/Y, h:mm a')} - ${moment(
          batch.endDate
        ).format('DD/MM/Y, h:mm a')}`,
    },
    {
      key: 'requestedBy',
      label: 'Requested By',
      render: (value, batch) => batch.requestedBy?.username,
    },
    {
      key: 'dateCreated',
      label: 'Date Created',
      width: '170px',
      render: (value, batch) =>
        moment(batch.dateCreated).format('DD MMM YY, h:mm a'),
    },
    {
      key: 'status',
      label: 'Status',
      render: (value, batch) => (
        <Tag variant={STATUS_TAG_VARIANT[batch.status] || 'neutral'}>
          {batch.status}
        </Tag>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      width: '230px',
      render: (value, batch) => (
        <div className="flex items-center gap-xs">
          <Button
            size="sm"
            variant="outline"
            onClick={createShowBatchItemsHandler(batch.id)}>
            View Advances
          </Button>
          {batch.status === BatchStatus.Ready && (
            <Button
              size="sm"
              variant="primary"
              onClick={createDownloadHandler(batch.id)}>
              Download CSV
            </Button>
          )}
        </div>
      ),
    },
  ];

  const emptyState = (
    <EmptyState
      icon="fas fa-layer-group"
      message="No advance batches found"
    />
  );

  return (
    <Card className="shadow border-0">
      <div className="table-responsive">
        <DataTable
          columns={columns}
          data={safeAdvanceBatches}
          loading={isLoading}
          emptyState={emptyState}
          skeletonRows={8}
        />
      </div>
    </Card>
  );
};

AdvanceBatchList.propTypes = {
  advanceBatches: PropTypes.arrayOf(PropTypes.object),
  handleShowBatchItemsModal: PropTypes.func,
  selectedEmployerId: PropTypes.string,
  onBulkSelectionChange: PropTypes.func,
  bulkSelection: PropTypes.object,
  isLoading: PropTypes.bool,
};

AdvanceBatchList.defaultProps = {
  advanceBatches: [],
  handleShowBatchItemsModal: () => undefined,
  selectedEmployerId: '',
  onBulkSelectionChange: () => undefined,
  bulkSelection: null,
  isLoading: false,
};

export default AdvanceBatchList;
